import { useCallback, useMemo } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { addMonths } from "date-fns";
import { useAppSelector } from "@redux/hooks";
import { selectTeacherProfileRetrieval } from "@user-management-context/read/application/use-cases/teacher/teacher-profile-retrieval/selectors/teacher-profile-selectors";
import { selectDocumentUpload } from "@shared-kernel/application/use-cases/document-upload/selectors/document-upload-selectors";
import { commonAdapters } from "src/common-adapters";
import { PayoutRequestDialog } from "./payout-request-dialog";
import { usePayoutRequest } from "./use-payout-request";
import {
  createPayoutRequestSchema,
  defaultPayoutRequestValues,
  type PayoutRequestFormInputs,
} from "./form-validation/payout-request.schema";

interface Props {
  parentDialogOnOpenChange: (open: boolean) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  hours: number;
  amount: number;
}

export const PayoutRequestDialogContainer = ({ open, onOpenChange, parentDialogOnOpenChange, hours, amount }: Props) => {
  const { dateProvider } = commonAdapters;
  const now = useMemo(() => dateProvider!.now(), [dateProvider]);
  const { data: profile } = useAppSelector(selectTeacherProfileRetrieval);
  const { processing } = useAppSelector(selectDocumentUpload);
  const defaultValues = useMemo(() => ({ ...defaultPayoutRequestValues, invoiceDate: now, invoiceDeadline: addMonths(now, 1) }), [now]);
  const form = useForm<PayoutRequestFormInputs>({ resolver: yupResolver(createPayoutRequestSchema(now)), defaultValues });
  const closeDialogs = useCallback(() => {
    onOpenChange(false);
    parentDialogOnOpenChange(false);
  }, [onOpenChange, parentDialogOnOpenChange]);
  const { request, uploadInvoice } = usePayoutRequest(form, closeDialogs);

  const handleOpenChange = (isOpen: boolean) => {
    if (form.formState.isSubmitting) return;
    if (!isOpen) form.reset(defaultValues);
    onOpenChange(isOpen);
  };

  if (!profile) return null;

  return (
    <PayoutRequestDialog
      open={open}
      onOpenChange={handleOpenChange}
      hours={hours}
      amount={amount}
      profile={profile}
      now={now}
      form={form}
      uploadProcessing={processing}
      onSubmit={form.handleSubmit(request)}
      onUpload={uploadInvoice}
    />
  );
};
